// Skeleton.tsx

import React, { type CSSProperties } from "react";

interface SkeletonProps {
    /**
     * Additional class names to apply to the skeleton
     */
    className?: string;

    /**
     * Base color of the skeleton
     * @default "#e2e8f0"
     */
    baseColor?: string;

    /**
     * Opacity of the base color (0-1)
     * @default 1
     */
    opacity?: number;

    /**
     * Highlight color for the animation
     * @default "#f8fafc"
     */
    highlightColor?: string;

    /**
     * Percentage to lighten/darken the highlight color relative to the base color
     * Positive values make it lighter, negative values make it darker (-100 to +100)
     * This overrides highlightColor if provided
     */
    highlightVariation?: number;

    /**
     * Animation speed in seconds
     * @default 1.5
     */
    animationSpeed?: number;

    /**
     * Width of the skeleton
     * If not provided, skeleton will take the full width of its parent
     */
    width?: string | number;

    /**
     * Height of the skeleton
     * If not provided, skeleton will take a default height
     * @default "1rem"
     */
    height?: string | number;

    /**
     * Border radius of the skeleton
     * @default "0.25rem" (4px)
     */
    borderRadius?: string | number;

    /**
     * Whether the skeleton is circular
     * @default false
     */
    circle?: boolean;

    /**
     * Custom CSS styles to apply to the skeleton
     */
    styles?: CSSProperties;

    /**
     * Disable animation (useful for reduced motion preferences)
     * @default false
     */
    noAnimation?: boolean;
}

/**
 * Convert hex color to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    const hexColor = hex.startsWith("#") ? hex.slice(1) : hex;
    const r = parseInt(hexColor.slice(0, 2), 16);
    const g = parseInt(hexColor.slice(2, 4), 16);
    const b = parseInt(hexColor.slice(4, 6), 16);
    return { r, g, b };
};

/**
 * Convert RGB values back to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
    return (
        "#" +
        [r, g, b]
            .map((x) => {
                const hex = Math.round(x).toString(16);
                return hex.length === 1 ? "0" + hex : hex;
            })
            .join("")
    );
};

/**
 * Convert RGB values to rgba color string
 */
const rgbToRgba = (r: number, g: number, b: number, a: number): string => {
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
};

/**
 * Apply highlight variation to a color
 */
const applyHighlightVariation = (
    baseColor: string,
    variation: number,
    opacity: number = 1,
): string => {
    const rgb = hexToRgb(baseColor);

    if (variation > 0) {
        const factor = variation / 100;
        rgb.r = rgb.r + (255 - rgb.r) * factor;
        rgb.g = rgb.g + (255 - rgb.g) * factor;
        rgb.b = rgb.b + (255 - rgb.b) * factor;
    } else if (variation < 0) {
        const factor = Math.abs(variation) / 100;
        rgb.r = rgb.r * (1 - factor);
        rgb.g = rgb.g * (1 - factor);
        rgb.b = rgb.b * (1 - factor);
    }

    return opacity < 1
        ? rgbToRgba(rgb.r, rgb.g, rgb.b, opacity)
        : rgbToHex(rgb.r, rgb.g, rgb.b);
};

// Static styles - inject once globally
const SKELETON_STYLES_ID = "skeleton-global-styles";
if (
    typeof document !== "undefined" &&
    !document.getElementById(SKELETON_STYLES_ID)
) {
    const style = document.createElement("style");
    style.id = SKELETON_STYLES_ID;
    style.textContent = `
        @keyframes skeleton-shimmer {
            0% {
                transform: translateX(-100%);
            }
            100% {
                transform: translateX(100%);
            }
        }
        
        .skeleton-container {
            position: relative;
            overflow: hidden;
            contain: layout style paint;
            will-change: contents;
        }
        
        .skeleton-shimmer {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(255, 255, 255, 0.4) 50%,
                transparent 100%
            );
            transform: translateX(-100%);
            will-change: transform;
        }
        
        .skeleton-shimmer-animated {
            animation: skeleton-shimmer var(--animation-speed, 1.5s) ease-in-out infinite;
        }
        
        @media (prefers-reduced-motion: reduce) {
            .skeleton-shimmer-animated {
                animation: none;
            }
        }
    `;
    document.head.appendChild(style);
}

const Skeleton: React.FC<SkeletonProps> = ({
    className = "",
    baseColor = "#e2e8f0",
    opacity = 1,
    highlightColor = "#f8fafc",
    highlightVariation,
    animationSpeed = 1.5,
    width,
    height = "1rem",
    borderRadius = "0.25rem",
    circle = false,
    styles = {},
    noAnimation = false,
}) => {
    // Apply opacity to base color if needed
    const baseColorWithOpacity =
        opacity < 1
            ? rgbToRgba(
                  hexToRgb(baseColor).r,
                  hexToRgb(baseColor).g,
                  hexToRgb(baseColor).b,
                  opacity,
              )
            : baseColor;

    // Calculate highlight color based on variation if provided
    const computedHighlightColor =
        highlightVariation !== undefined
            ? applyHighlightVariation(baseColor, highlightVariation, opacity)
            : opacity < 1
              ? rgbToRgba(
                    hexToRgb(highlightColor).r,
                    hexToRgb(highlightColor).g,
                    hexToRgb(highlightColor).b,
                    opacity,
                )
              : highlightColor;

    // Convert number values to pixel strings
    const getSize = (size: string | number | undefined) => {
        if (size === undefined) return undefined;
        return typeof size === "number" ? `${size}px` : size;
    };

    const widthValue = getSize(width);
    const heightValue = getSize(height);
    const radiusValue = getSize(circle ? "50%" : borderRadius);

    // Container styles
    const containerStyle: CSSProperties = {
        display: "block",
        width: widthValue || "100%",
        height: circle ? widthValue || heightValue : heightValue,
        borderRadius: radiusValue,
        backgroundColor: baseColorWithOpacity,
        ...styles,
        // CSS custom property for animation speed
        "--animation-speed": `${animationSpeed}s`,
    } as CSSProperties;

    // Shimmer overlay styles
    // const shimmerStyle: CSSProperties = {
    //     background: `linear-gradient(
    //         90deg,
    //         transparent 0%,
    //         ${computedHighlightColor} 50%,
    //         transparent 100%
    //     )`,
    // };

    const shimmerStyle: CSSProperties = {
        background: `linear-gradient(
            105deg,
            transparent 40%,
            ${computedHighlightColor} 50%,
            transparent 60%
        )`,
        // Make the shimmer effect larger for better visibility
        width: "200%",
        left: "-100%",
    };

    return (
        <div
            className={`skeleton-container ${className}`}
            style={containerStyle}
            role="status"
            aria-label="Loading..."
        >
            <div
                className={`skeleton-shimmer ${
                    !noAnimation ? "skeleton-shimmer-animated" : ""
                }`}
                style={shimmerStyle}
            />
        </div>
    );
};

export default Skeleton;
